import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { NotifDetailModal } from '../components/PushNotificationManager';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import {
  countUnreadByCategory,
  filterNotifsByCategory,
  NOTICE_BODY_PREVIEW_STYLE,
  resolvePushTypeForSave,
} from '../utils/pushNotifCategory';

function getAuthToken() {
  try {
    const stored = JSON.parse(localStorage.getItem('user') || '{}');
    return stored?.token ? String(stored.token) : '';
  } catch { return ''; }
}
function authHdr() {
  const t = getAuthToken();
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}
function fmtShort(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return `${d.getFullYear()}.${String(d.getMonth()+1).padStart(2,'0')}.${String(d.getDate()).padStart(2,'0')} `
         + `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
  } catch { return iso; }
}
function mapPersonalPushItem(n) {
  const title = n.title || 'MRT 공지';
  let pushType = n.pushType || 'broadcast';
  if (pushType === 'individual') {
    pushType = resolvePushTypeForSave({ title, kind: '' });
  }
  return {
    seq: n.seq,
    title,
    body: n.body || '',
    pushType,
    extSeq: n.extSeq || null,
    read: !!n.isRead,
    receivedAt: n.receivedAt,
    readAt: n.readAt,
    pushData: { kind: '' },
  };
}

function NotifTableRows({ items, notifs, onRowClick, expandBody = false, openDetailOnClick = true }) {
  return items.map((n, idx) => (
    <tr key={n.seq}
      style={{ borderBottom: '1px solid #1a1a2e',
        background: n.read
          ? (idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)')
          : 'rgba(108,99,255,0.07)',
        cursor: openDetailOnClick ? 'pointer' : 'default' }}
      onMouseEnter={openDetailOnClick ? (e => { e.currentTarget.style.background = 'rgba(108,99,255,0.14)'; e.currentTarget.style.outline = '1px solid rgba(108,99,255,0.3)'; }) : undefined}
      onMouseLeave={openDetailOnClick ? (e => {
        e.currentTarget.style.background = n.read
          ? (idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)')
          : 'rgba(108,99,255,0.07)';
        e.currentTarget.style.outline = 'none';
      }) : undefined}
      onClick={openDetailOnClick ? () => onRowClick(n) : undefined}>

      <td style={{ padding: '10px 8px', textAlign: 'center', color: '#444455', fontSize: 11 }}>
        {notifs.length - notifs.indexOf(n)}
      </td>

      <td style={{ padding: '10px 10px', maxWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          {!n.read && (
            <span style={{ width: 6, height: 6, borderRadius: '50%',
              background: '#6c63ff', flexShrink: 0 }} />
          )}
          <span style={{
            fontWeight: n.read ? 500 : 700,
            color: n.read ? '#9999bb' : '#e8e8f0',
            ...(expandBody
              ? { wordBreak: 'break-word' }
              : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }),
          }}>
            {n.title}
          </span>
          {!expandBody && (
            <span style={{
              fontSize: 10, fontWeight: 700, flexShrink: 0, borderRadius: 4, padding: '1px 5px',
              color: '#fbbf24',
              background: 'rgba(251,191,36,0.12)',
            }}>
              결재
            </span>
          )}
        </div>
        <div style={{
          fontSize: 12,
          color: expandBody ? '#8888aa' : '#555566',
          paddingLeft: n.read ? 0 : 12,
          ...(expandBody
            ? NOTICE_BODY_PREVIEW_STYLE
            : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }),
        }}>
          {n.body || (expandBody ? '(내용 없음)' : '')}
        </div>
      </td>

      <td style={{ padding: '10px 10px', color: '#8888aa', fontSize: 12, whiteSpace: 'nowrap' }}>
        {fmtShort(n.receivedAt)}
      </td>

      <td style={{ padding: '10px 10px', textAlign: 'center', color: '#555566', fontSize: 11, whiteSpace: 'nowrap' }}>
        {n.read && n.readAt ? fmtShort(n.readAt) : n.read ? '—' : ''}
      </td>

      <td style={{ padding: '10px 8px', textAlign: 'center' }}>
        {n.read ? (
          <span style={{ fontSize: 11, fontWeight: 600, color: '#4ade80',
            background: 'rgba(74,222,128,0.1)', borderRadius: 6,
            padding: '3px 7px', display: 'inline-block' }}>
            확인함
          </span>
        ) : (
          <button
            onClick={(e) => { e.stopPropagation(); onRowClick(n); }}
            style={{ fontSize: 11, fontWeight: 700, color: '#fff',
              background: 'linear-gradient(135deg,#6c63ff,#5a52d5)',
              border: 'none', borderRadius: 6, padding: '3px 8px', cursor: 'pointer' }}>
            확인
          </button>
        )}
      </td>
    </tr>
  ));
}

/* ── 전체공지 + 개인알림 합치기 ────────────────────────────────── */
function mergeNotifs(personal, broadcasts) {
  const usedExtSeqs = new Set(personal.map(n => n.extSeq).filter(Boolean));
  const broadcastItems = broadcasts
    .filter(b => !usedExtSeqs.has(b.seq))
    .map(b => ({
      seq: `b_${b.seq}`,
      extSeq: b.seq,
      title: b.title || 'MRT 공지',
      body: b.body || '',
      pushType: 'broadcast',
      read: !!b.isRead,
      receivedAt: b.createdAt,
      readAt: b.readAt || null,
      isBroadcastOnly: true,
    }));
  const all = [...personal, ...broadcastItems];
  all.sort((a, b2) => new Date(b2.receivedAt || 0) - new Date(a.receivedAt || 0));
  return all;
}

const S = {
  page: { minHeight: '100vh', background: 'var(--bg-primary, #0f0f1a)', color: 'var(--text-primary, #e8e8f0)' },
  content: { maxWidth: 860, margin: '0 auto', padding: '24px 16px 60px' },
  backBtn: { background: 'none', border: 'none', color: 'var(--text-secondary, #8888aa)', cursor: 'pointer',
    fontSize: 13, padding: '8px 0', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 4 },
  card: { background: 'var(--bg-card, #1a1a2e)', border: '1px solid var(--border-color, #2a2a3e)',
    borderRadius: 12, padding: '20px 24px', marginBottom: 20 },
  titleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 },
  title: { fontSize: 16, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8 },
  badge: { background: '#ef4444', color: '#fff', borderRadius: 10, fontSize: 11, fontWeight: 700,
    padding: '2px 7px' },
  toolRow: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  filterBtn: { padding: '5px 14px', borderRadius: 20, border: '1px solid #2a2a3e',
    fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' },
};

export default function MyNotificationsPage() {
  const navigate = useNavigate();
  const apiUrl = getApiBaseUrl();

  const [notifs, setNotifs] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all' | 'unread' | 'read'
  const [categoryTab, setCategoryTab] = useState('notice'); // 'notice' | 'approval'
  const [detail, setDetail] = useState(null);  // 상세 보기 모달

  // ── 목록 불러오기 (개인 수신함 + 전체공지 합치기) ─────────────
  const fetchNotifs = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    setLoading(true);
    try {
      const [resPersonal, resBroadcast] = await Promise.all([
        fetch(`${apiUrl}/api/push/my?limit=200`, { headers: authHdr() }),
        fetch(`${apiUrl}/api/erp/push/my-broadcasts?limit=100`, { headers: authHdr() }),
      ]);
      const personalData = resPersonal.ok ? await resPersonal.json() : { items: [] };
      const broadcastData = resBroadcast.ok ? await resBroadcast.json() : { items: [] };

      const personal = (personalData.items || []).map(mapPersonalPushItem);
      const merged = mergeNotifs(personal, broadcastData.items || []);
      setNotifs(merged);
      setUnreadCount(merged.filter(n => !n.read).length);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [apiUrl]);

  useEffect(() => { fetchNotifs(); }, [fetchNotifs]);

  // ── 단건 읽음 처리 ────────────────────────────────────────────
  const markRead = useCallback(async (notif) => {
    if (notif.read) return;
    try {
      if (notif.isBroadcastOnly) {
        // push_list에 없는 전체공지 → ERP 읽음만
        if (notif.extSeq) {
          await fetch(`${apiUrl}/api/erp/push/read`, {
            method: 'POST', headers: authHdr(),
            body: JSON.stringify({ push_seq: notif.extSeq }),
          });
        }
      } else {
        await fetch(`${apiUrl}/api/push/read`, {
          method: 'POST', headers: authHdr(),
          body: JSON.stringify({ seq: notif.seq }),
        });
        if (notif.extSeq) {
          await fetch(`${apiUrl}/api/erp/push/read`, {
            method: 'POST', headers: authHdr(),
            body: JSON.stringify({ push_seq: notif.extSeq }),
          }).catch(() => {});
        }
      }
    } catch { /* ignore */ }
    setNotifs(prev => prev.map(n => n.seq === notif.seq ? { ...n, read: true, readAt: new Date().toISOString() } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, [apiUrl]);

  // ── 전체 읽음 ─────────────────────────────────────────────────
  const readAll = useCallback(async () => {
    try {
      await fetch(`${apiUrl}/api/push/read_all`, { method: 'POST', headers: authHdr() });
    } catch { /* ignore */ }
    // broadcast-only 미읽음 → ERP 읽음 처리
    const unreadBroadcasts = notifs.filter(n => !n.read && n.isBroadcastOnly && n.extSeq);
    await Promise.all(unreadBroadcasts.map(n =>
      fetch(`${apiUrl}/api/erp/push/read`, {
        method: 'POST', headers: authHdr(),
        body: JSON.stringify({ push_seq: n.extSeq }),
      }).catch(() => {})
    ));
    setNotifs(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  }, [apiUrl, notifs]);

  // ── 필터링 ────────────────────────────────────────────────────
  const byReadStatus = notifs.filter(n => {
    if (filter === 'unread') return !n.read;
    if (filter === 'read') return n.read;
    return true;
  });
  const displayed = filterNotifsByCategory(byReadStatus, categoryTab);

  const CATEGORY_TABS = [
    { key: 'notice', label: `공지사항${countUnreadByCategory(notifs, 'notice') ? ` (${countUnreadByCategory(notifs, 'notice')})` : ''}` },
    { key: 'approval', label: `결재${countUnreadByCategory(notifs, 'approval') ? ` (${countUnreadByCategory(notifs, 'approval')})` : ''}` },
  ];

  const FILTERS = [
    { key: 'all',    label: '전체' },
    { key: 'unread', label: `미확인 ${unreadCount > 0 ? `(${unreadCount})` : ''}` },
    { key: 'read',   label: '확인함' },
  ];

  return (
    <div style={S.page}>
      <Header />
      <div style={S.content}>
        <button style={S.backBtn} onClick={() => navigate('/')}>← 대시보드로</button>

        <div style={S.card}>
          {/* 상단 타이틀 + 버튼 */}
          <div style={S.titleRow}>
            <div style={S.title}>
              🔔 내 알림 내역
              {unreadCount > 0 && <span style={S.badge}>{unreadCount}</span>}
            </div>
            <div style={S.toolRow}>
              {unreadCount > 0 && (
                <button
                  onClick={readAll}
                  style={{ ...S.filterBtn, background: 'rgba(108,99,255,0.2)',
                    border: '1px solid rgba(108,99,255,0.4)', color: '#a09af8' }}>
                  전체 읽음
                </button>
              )}
              <button
                onClick={fetchNotifs}
                style={{ ...S.filterBtn, background: 'none', color: '#8888aa' }}>
                🔄 새로고침
              </button>
            </div>
          </div>

          {/* 카테고리 탭 */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
            {CATEGORY_TABS.map((tab) => (
              <button key={tab.key} onClick={() => setCategoryTab(tab.key)}
                style={{ ...S.filterBtn,
                  background: categoryTab === tab.key ? 'linear-gradient(135deg,#6c63ff,#5a52d5)' : '#12121f',
                  border: categoryTab === tab.key ? '1px solid #6c63ff' : '1px solid #2a2a3e',
                  color: categoryTab === tab.key ? '#fff' : '#8888aa' }}>
                {tab.label}
              </button>
            ))}
          </div>

          {/* 읽음 상태 탭 */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
            {FILTERS.map(f => (
              <button key={f.key} onClick={() => setFilter(f.key)}
                style={{ ...S.filterBtn,
                  background: filter === f.key ? 'linear-gradient(135deg,#6c63ff,#5a52d5)' : '#12121f',
                  border: filter === f.key ? '1px solid #6c63ff' : '1px solid #2a2a3e',
                  color: filter === f.key ? '#fff' : '#8888aa' }}>
                {f.label}
              </button>
            ))}
          </div>

          {/* 표 */}
          {loading ? (
            <div style={{ padding: '40px 0', textAlign: 'center', color: '#8888aa', fontSize: 14 }}>
              불러오는 중…
            </div>
          ) : displayed.length === 0 ? (
            <div style={{ padding: '40px 0', textAlign: 'center', color: '#8888aa', fontSize: 14 }}>
              {filter === 'unread' ? '미확인 알림이 없습니다 ✓'
                : categoryTab === 'approval' ? '결재 알림이 없습니다'
                : categoryTab === 'notice' ? '공지사항이 없습니다'
                : '알림 내역이 없습니다'}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '5%' }} />
                  <col style={{ width: '52%' }} />
                  <col style={{ width: '17%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '13%' }} />
                </colgroup>
                <thead>
                  <tr style={{ background: '#12121f', borderBottom: '2px solid #2a2a3e' }}>
                    {['#', categoryTab === 'notice' ? '제목 · 내용 (3줄)' : '제목 · 내용 (클릭하면 전체 보기)', '수신일시', '확인일시', '상태'].map((h, i) => (
                      <th key={i} style={{ padding: '9px 10px',
                        textAlign: i === 0 ? 'center' : i >= 2 ? 'center' : 'left',
                        color: '#555566', fontWeight: 700, fontSize: 11,
                        letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <NotifTableRows
                    items={displayed}
                    notifs={notifs}
                    expandBody={categoryTab === 'notice'}
                    openDetailOnClick={categoryTab === 'approval'}
                    onRowClick={(n) => {
                      if (categoryTab === 'approval') setDetail(n);
                      if (!n.read) markRead(n);
                    }}
                  />
                </tbody>
              </table>
            </div>
          )}

          {!loading && notifs.length > 0 && (
            <div style={{ marginTop: 14, fontSize: 12, color: '#444455', textAlign: 'right' }}>
              총 {notifs.length}개 · 미확인 {unreadCount}개 · 확인함 {notifs.length - unreadCount}개
            </div>
          )}
        </div>
      </div>

      {/* 상세 모달 */}
      <NotifDetailModal notif={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
